<template>
  <div class="feature-landing-page" id="main-content" ref="main-content">
    <div class="content-page-banner">
      <div class="banner-content">
        <h1>{{ $route.meta.title }}</h1>
        <div class="underline"></div>
        <div id="" v-html="referenceData.primaryLimitRefData.informationText" />
      </div>
    </div>
    <Alert
      v-if="(apiStatus.refData.fail && !apiStatus.refData.loading) || (apiStatus.memberHistory.fail && !apiStatus.memberHistory.loading) || apiStatus.recordData.fail"
      alertType="error"
    />
    <Tabs id="limits-usage-tabs" :tabs="tabData">
      <template v-slot="{ activeTab }">
        <ApiLoading
          class="api-loading"
          v-if="apiStatus.refData.loading || apiStatus.memberHistory.loading || apiStatus.refData.fail || apiStatus.memberHistory.fail"
          :apiError="apiStatus.refData.fail || apiStatus.memberHistory.fail"
        />
        <template v-else>
          <!-- Disable hospital table in develop -->
          <!-- Hospital Services Table -->
          <!-- <div v-if="activeTab === 1">
            <HospitalLimitsUsage
              v-if="showHospitalLimits"
              :refDataApiStatus="apiStatus.refData"
              :referenceData="referenceData"
              :tableRefData="tableRefData"
              @recordDataFail="setRecordDataFail"
            >
              <template #filterHeader><h3>Filter limits and usage by:</h3></template>
            </HospitalLimitsUsage>
          </div> -->
          <!-- Extras Services Table -->
          <div v-if="activeTab === 0">
            <ExtrasLimitsUsage
              v-if="showExtrasLimits"
              :refDataApiStatus="apiStatus.refData"
              :referenceData="referenceData"
              :tableRefData="tableRefData"
              :currentDeviceLayout="currentDevice"
              @recordDataFail="setRecordDataFail"
            >
              <template #paragraphData>
                <section class="top-content fr-view" v-if="!apiStatus.refData.loading && !apiStatus.memberHistory.fail">
                  <div
                    class="text-container"
                    v-if="referenceData.primaryLimitRefData.callOutText && !checkSomeObjectPropertiesTrue(apiStatus.refData)"
                    v-html="referenceData.primaryLimitRefData.callOutText"
                  />
                </section>
              </template>
              <template #filterHeader><h3>Filter limits and usage by:</h3></template>
            </ExtrasLimitsUsage>
            <section>
              <p class="pre-footer" v-html="referenceData.preFooterRefData.informationText" />
            </section>
          </div>
        </template>
      </template>
    </Tabs>
  </div>
</template>

<script>
import { Alert, ApiLoading } from '@/components';
import { ProductType } from '@general/js/enums';
import { DeviceTypes, AccessLevel } from '@/enums';
import { checkSomeObjectPropertiesTrue } from '@general/js/utils';
import { isBuildCRM, replaceTokenInString, getCurrentDeviceLayout } from '@/utils';
import HomePageServices from '@general/js/services/HomePage';
import MemberDetailsServices from '@/services/MemberDetails';
import LimitsUsageServices from '@/services/MyClaims/LimitsUsage';
import Tabs from '@general/js/components/Tabs.vue';
import ExtrasLimitsUsage from './ExtrasLimitsUsage.vue';
// import HospitalLimitsUsage from './HospitalLimitsUsage.vue';
import LimitsUsageMixin from './LimitsUsageMixin';

const isCRM = isBuildCRM();

export default {
  name: 'LimitsUsage',

  components: {
    Alert,
    ApiLoading,
    ExtrasLimitsUsage,
    // HospitalLimitsUsage,
    Tabs,
  },

  mixins: [LimitsUsageMixin],

  data() {
    return {
      apiStatus: {
        refData: {
          fail: false,
          loading: false,
        },
        recordData: {
          fail: false, // Use fail to show the top alert error message
          loading: false,
        },
        memberHistory: {
          fail: false,
          loading: false,
        },
      },
      referenceData: {
        primaryLimitRefData: {
          informationText: null,
          callOutText: null,
        },
        hospitalServicesRefData: {
          informationText: null,
        },
        extrasServicesRefData: {
          informationText: null,
        },
        replacementPeriodRefData: {
          informationText: null,
        },
        preFooterRefData: {
          informationText: null,
        },
      },
      tableRefData: {
        consumptionOnly: null,
        subLimits: null,
        replacementPeriods: null,
      },
      currentDevice: null,
      tabData: [
        { title: 'Extras' },
        // { title: 'Hospital' }
      ],
      // showHospitalLimits: false,
      showExtrasLimits: false,
      isCRM,
      DeviceTypes,
    };
  },

  beforeMount() {
    this.currentDevice = getCurrentDeviceLayout(window.innerWidth);
    this.fetchReferenceData();
    this.fetchMemberHistory();
  },

  mounted() {
    window.addEventListener('resize', this.resizeHandler);
  },

  unmounted() {
    window.removeEventListener('resize', this.resizeHandler);
  },

  // Need to create the mounted when it should load the data from initial state
  methods: {
    resizeHandler() {
      // This is to check whether the current device layout when resized is in mobile view size to show 'Return to top' nav links
      const resizeDebounceTimeout = 250;
      setTimeout(() => {
        this.currentDevice = getCurrentDeviceLayout(window.innerWidth);
      }, resizeDebounceTimeout);
    },

    async fetchReferenceData() {
      this.apiStatus.refData.loading = true;
      this.apiStatus.refData.fail = false;

      try {
        const refDataServices = [LimitsUsageServices.getReferenceData()];
        // Call getMembershipInfo to get the productGuideLink if it does not exist yet
        if (!this.membershipInfo?.productGuideLink) {
          refDataServices.push(HomePageServices.getMembershipInfo(this.policyNumber));
        }
        const [refData, membershipInfoRes] = await Promise.all(refDataServices);

        // Save membershipInfo to Vuex store if membershipInfo does not exist yet
        if (!this.membershipInfo?.productGuideLink) {
          this.updateMembershipInfo(membershipInfoRes);
        }

        if (refData && ((this.membershipInfo && this.membershipInfo.productGuideLink) || (membershipInfoRes && membershipInfoRes.productGuideLink))) {
          const productGuideLink = this.membershipInfo.productGuideLink ?? membershipInfoRes.productGuideLink;
          // Replace #{productGuideLink} token with valid URL from membershipInfo
          // DE2E-8479: Need to treat '\'' as literal as Kentico sends through URL in the format \#{productGuideLink}\
          const updatedInformationText = replaceTokenInString(refData.limitsAndUsageInformationText.informationText, /\\#{productGuideLink}\\/g, productGuideLink);
          // Update referenceData
          this.referenceData = {
            ...this.referenceData,
            primaryLimitRefData: {
              informationText: updatedInformationText,
              callOutText: refData.limitsAndUsageCalloutText.informationText,
            },
            // hospitalServicesRefData: refData.limitsAndUsageHospitalServicesInformationText,
            extrasServicesRefData: refData.limitsAndUsageExtrasServicesInformationText,
            limitsAndUsageFlexiLimitInformationText: refData.limitsAndUsageFlexiLimitInformationText,
            limitsAndUsageHealthAndWellbeingInformationText: refData.limitsAndUsageHealthAndWellbeingInformationText,
            limitsAndUsageMedicallyPrescribedDevicesInformationText: refData.limitsAndUsageMedicallyPrescribedDevicesInformationText,
            limitsAndUsageNonCosmeticProsthesesInformationText: refData.limitsAndUsageNonCosmeticProsthesesInformationText,
            replacementPeriodRefData: refData.limitsAndUsageThreeYearBenefitInformationText,
            preFooterRefData: refData.limitsAndUsagePreFooterInformationText,
          };

          // Update tableRefData to get the table structure for those options that have additional tables
          this.tableRefData = {
            ...this.tableRefData,
            consumptionOnly: refData.consumptionOnlyOptions,
            subLimits: refData.subLimitsOptions,
            replacementPeriods: refData.replacementPeriodsOptions,
          };
        }
      } catch (error) {
        this.apiStatus.refData.fail = true;
        logger.error(error);
      } finally {
        this.apiStatus.refData.loading = false;
      }
    },

    async fetchMemberHistory() {
      this.apiStatus.memberHistory.loading = true;
      this.apiStatus.memberHistory.fail = false;

      try {
        const memberDataServices = [LimitsUsageServices.getMemberHistory(this.policyNumber)];
        // Call member details to get the coveredOnMembership for member dropdowns
        if (!this.members) {
          memberDataServices.push(MemberDetailsServices.getMembers(this.policyNumber));
        }

        const [memberHistoryRes, memberList] = await Promise.all(memberDataServices);
        if (memberList) {
          this.updateMembers(memberList);
        }

        this.updateMemberHistory(memberHistoryRes);

        // Find if there's existing hospital and extras product in the product history, then display respective table if true
        if (memberHistoryRes) {
          this.updateMembers(
            this.members.filter((member) => {
              return memberHistoryRes.some((memberHistory) => {
                return memberHistory.personCode === member.personCode;
              });
            }),
          );
          if (this.accessLevel === AccessLevel.POLICY_HOLDER_ACCESS || this.accessLevel === AccessLevel.FULL_ACCESS) {
            // Check all policy product history whether they have a hospital or extras product if logged member is policy holder or full access
            // this.showHospitalLimits = memberHistoryRes.some((memberHistoryItem) => {
            //   const products = memberHistoryItem.products;
            //   return products.some((productHistoryItem) => {
            //     return [ProductType.HOSPITAL_ONLY, ProductType.PACKAGE].includes(productHistoryItem.type.toLowerCase());
            //   });
            // });
            this.showExtrasLimits = memberHistoryRes.some((memberHistoryItem) => {
              const products = memberHistoryItem.products;
              return products.some((productHistoryItem) => {
                return [ProductType.EXTRAS_ONLY, ProductType.PACKAGE].includes(productHistoryItem.type.toLowerCase());
              });
            });
          } else {
            // Get the accessing person member history
            const accessingPersonHistory = memberHistoryRes.find((member) => {
              return member.personCode === this.currentMemberDetail.personCode;
            });

            // Find if there is a hospital product in the history data, return true if it's a package product
            // this.showHospitalLimits = accessingPersonHistory?.products.some((productHistoryItem) => {
            //   return [ProductType.HOSPITAL_ONLY, ProductType.PACKAGE].includes(productHistoryItem.type.toLowerCase());
            // });

            // Find if there is an extras product in the history data, return true if it's a package product
            this.showExtrasLimits = accessingPersonHistory?.products.some((productHistoryItem) => {
              return [ProductType.EXTRAS_ONLY, ProductType.PACKAGE].includes(productHistoryItem.type.toLowerCase());
            });
          }
        }
      } catch (error) {
        this.apiStatus.memberHistory.fail = true;
        logger.error(error);
      } finally {
        this.apiStatus.memberHistory.loading = false;
      }
    },

    setRecordDataFail() {
      this.apiStatus.recordData.fail = true;
    },

    checkSomeObjectPropertiesTrue,
  },
};
</script>

<style lang="scss" scoped>
.page-content {
  margin-top: 56px;
  max-width: 680px;
}

// Shared styles between hospital and extras record views
:deep(.records-view) {
  .message {
    text-align: center; // Center no results message in record views
  }

  .top-content {
    margin-top: 1.5rem;
  }
}

:deep(.limits-usage-records) {
  margin-top: rem(40);
}

.pre-footer {
  margin-top: rem(110);
}
</style>
